import PropTypes from 'prop-types';
import React from 'react';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import sortBy from 'lodash/sortBy';
import throttle from 'lodash/throttle';
import min from 'lodash/min';

import customerTimelineApi from 'api/timeline';
import customerNotesApi from 'api/customerNotes';

import TimelineModel from 'models/timeline';
import TimelineEventErrorHandler from './timelineEventErrorHandler';
import TimelineEvent from './timelineEvent';
import TimelineNote from './timelineNote';
import Loader from 'components/loader';
import {FormattedMessage, intlShape, injectIntl} from 'react-intl';

import {getTypeColor, getFiltered, getDetailsLink} from 'helpers/timelineFiltering';
import {typeEventFilters, POSTypes, noteTypeFilters, allNoteTypes} from 'helpers/timelineConstants';

// Libs
import events from 'lib/events';
import {chemicalBaseTypeNames, dateTimeFormats} from 'helpers/constants';
import Note from 'models/note';
import {saveNote} from 'api/note';
import {newNotesEnabled} from 'features';
import EditableTextField from 'components/forms/editableTextField';
import Col from 'components/Col';
import Row from 'components/Row';
import EditableSelectField from 'components/forms/editableSelectField';
import {noteTypes} from 'components/notes/NoteModal';
import EditableTextArea from 'components/forms/editableTextArea';
import ValidateGroup from 'helpers/validation';
import analysisApi from 'api/analysis';
import waterTesterApi from 'api/waterTester';
import poolApi from 'api/pool';
import {TimelineType, TimelineAction} from 'models/enums/timeline';


class Timeline extends React.Component {
  static displayName = 'Timeline';

  static propTypes = {
    customer: PropTypes.any,
    id: PropTypes.any,
    intl: intlShape.isRequired,
    pos: PropTypes.any,
  };

  state = {
    timelinePageSize: 20,
    noteActive: false,
    loaded: false,
    timelinePageIndex: 0,
    timelineLoading: false,
    timelineGotAll: false,
    events: [],
    currentPools: [],
    currentCustomerContacts: [],

    showFilters: false,
    eventTypes: typeEventFilters(this.props.intl.formatMessage),
    noteTypes: noteTypeFilters(this.props.intl.formatMessage),
    eventFilter: '',
    noteFilter: '',
    contacts: [{id: null, label: null, email: null}],
    contactFilter: {id: '', label: '', email: ''},
    startDate: moment().subtract(6, 'month'),
    appliedFilters: null,
    endDate: moment(),
    sortByAsc: false,

    note: new Note({}),
    noteProperties: {
      type: 'customer',
      selectedAnalysis: undefined,
      selectedPool: undefined,
    },
  };

  componentWillMount() {
    const {customer} = this.props;

    this.getInitalTimelineEvents(false);

    const currentPools = (customer.pools || []).map(p => p.id);
    const currentCustomerContacts = (customer.customerContacts || []).map(c => ({
      name: `${c.nameFirst} ${c.nameLast}`,
      email: c.email,
    }));

    this.setState({
      currentPools: currentPools,
      currentCustomerContacts: currentCustomerContacts,
    });

    this.group = new ValidateGroup(this.saveNewNote);

    this.fetchData(this.props);
  }

  componentDidMount() {
    window.addEventListener('wheel', this.scrollGetTimeline, {capture: false, passive: true});
    window.addEventListener('scroll', this.scrollGetTimeline, {capture: false, passive: true});
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.customer.id !== this.props.customer.id) {
      this.fetchData(nextProps);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('wheel', this.scrollGetTimeline, {capture: false, passive: true});
    window.removeEventListener('scroll', this.scrollGetTimeline, {capture: false, passive: true});
  }

  fetchData = props => {
    const {customer} = props;
    const {formatMessage} = this.props.intl;

    Promise.all([
      poolApi.getList(customer.id),
      analysisApi.customerSummary(customer.id),
      waterTesterApi.getWaterTesters(),
    ]).then(([pools, analyses, waterTesters]) => {
      const note = this.state.note;

      waterTesters = [{
        key: -1,
        name: formatMessage({'id': 'timeline.notes.select.author'}),
      }, ...waterTesters];
      const defaultWaterTester =  waterTesters.filter(x => x.name)[0];

      note.author = defaultWaterTester ? defaultWaterTester.name : null;
      this.setState({pools, analyses, waterTesters, note});
    });
  };

  getEventLabel = event => {
    const {formatMessage} = this.props.intl;
    const {eventTypes} = this.state;

    return (eventTypes.find(e => event && e.types && e.types.toString() === event.toString()) ||
      {label: formatMessage({id: 'customer.timeline.all.events'})})
      .label;
  };

  updateNote = ({key, value}) => {
    const note = this.state.note;

    note[key] = value;

    this.setState({note});
  };

  updateNoteProperties = ({key, value}) => {
    const noteProperties = this.state.noteProperties;

    noteProperties[key] = value;

    if(key === 'type' && value === 'analysis') {
      noteProperties.selectedAnalysis = this.state.analyses && this.state.analyses.length ? this.state.analyses[0].id : undefined;
    }

    if(key === 'type' && value === 'system') {
      noteProperties.selectedPool = this.state.pools && this.state.pools.length ? this.state.pools[0].id : undefined;
    }

    this.setState({noteProperties});
  };

  editNote = (note) => {
    const noteProperties = {
      type: note.analysisID ? 'analysis' : note.systemID ? 'system' : 'customer',
      selectedAnalysis: note.analysisID,
      selectedPool: note.systemID,
    };

    note.name = note.title || note.Title;  //new note model || old model
    note.body = note.note || note.Note;

    this.setState({
      note: new Note({...note}),
      noteProperties,
      noteActive: true,
    });
  };

  getEvents = () => {
    const {pos} = this.props;
    let eventsArray = [];

    if (this.state.events.length > 0) {
      //Apply Filters
      let filteredEvents = getFiltered(this.state.events, [{filter: 'event', value: this.state.eventFilter}], pos);
      // Sort events by ASC/DESC

      if (this.state.noteFilter) {
        filteredEvents = getFiltered(filteredEvents, [{filter: 'event', value: this.state.noteFilter}], pos);
      }

      filteredEvents = sortBy(filteredEvents, 'created');

      if (!this.state.sortByAsc) {
        filteredEvents = filteredEvents.reverse();
      }

      //Filter dates and POS
      filteredEvents = filteredEvents.filter((t) => {
        if (!pos && POSTypes.includes(t.type)) {
          return false;
        }
        const start = moment(t.created).isAfter(this.state.startDate) || moment(t.created).isSame(this.state.startDate, 'day');
        const end = moment(t.created).isBefore(this.state.endDate) || moment(t.created).isSame(this.state.endDate, 'day');

        return start && end;
      });

      filteredEvents.forEach((t, i) => {
        eventsArray.push(
          <TimelineEventErrorHandler>
            <TimelineEvent
              key={i}
              event={t}
              scroll={this.state.scroll}
              color={getTypeColor(t.type)}
              editNote={this.editNote}
              customerContacts={this.state.currentCustomerContacts}
              link={getDetailsLink(t, this.state.currentPools)}
              onLastShow={i === filteredEvents.length - 1 ? this.lastChildVisible : null}
            />
          </TimelineEventErrorHandler>
        );
      });
    }
    return eventsArray;
  };

  getInitalTimelineEvents = (sort = false) => {
    this.setState({
      timelineLoading: true,
      events: [],
    });

    customerTimelineApi.getList(this.props.id, this.state.timelinePageSize, 0, sort)
      .then(timeline => {
        this.setState({
          events: [
            ...timeline,
            ...this.makeAnniversaryEvents(timeline.length, this.props.customer.created),
          ],
          loaded: true,
          timelinePageIndex: 0,
          sortByAsc: sort,
          startDate: this.getTimelineStartDate(timeline),
          timelineLoading: false,
        });
      })
      .catch(err => {
        console.error(err);
        events.publish('error.user', {message: this.props.intl.formatMessage({id: 'customer.error.get.timeline'})});
      });
  };

  // Gets earliest start date of array of timelines
  getTimelineStartDate = events => min(events.filter(e => e.type !== 68).map(e => e.created));


  lastChildVisible = () => {
    this.scrollGetTimeline(null, true);
  };

  // Generate fake anniversary events based on customer.created
  makeAnniversaryEvents = (events, date) => {
    const {formatMessage} = this.props.intl;
    let now = moment();
    let anDate = date.add(1, 'y');
    let count = 1;
    let anniversaries = [];

    while (anDate.isBefore(now)) {
      const anniversaryEvent = new TimelineModel({
        id: null,
        customerID: this.props.id,
        type: TimelineType.Anniversary,
        action: TimelineAction.Added,
        userEmail: 'System',
        data: {
          data: {},
          title: formatMessage({id: 'customer.timeline.year.anniversary'}, {year: count}),
          year: count,
        },
        created: anDate,
      });

      anniversaries.push(anniversaryEvent);
      anDate = anDate.add(1, 'y');
      count++;
    }

    return events > 0 ? anniversaries : [];
  };

  saveNewNote = () => {
    const note = this.state.note;
    const noteProperties = this.state.noteProperties;
    const defaultWaterTester =  this.state.waterTesters.filter(x => x.name)[0];

    note.customerID = this.props.customer.id;
    note.analysisID = noteProperties.selectedAnalysis;
    note.systemID = noteProperties.selectedPool;

    note.author = note.author || (defaultWaterTester ?  defaultWaterTester.name : null);

    return saveNote(note)
      .then(data => {
        if (!data) {
          return;
        }

        this.setState({
          note: new Note({}),
          noteActive: false,
          noteProperties: {
            type: 'customer',
            selectedAnalysis: undefined,
            selectedPool: undefined,
          },
        });
        this.getInitalTimelineEvents(false);
      })
      .catch(err => {
        console.error(err);
        events.publish('error.user', {message: this.props.intl.formatMessage({id: 'customer.error.save.notes'})});
      });
  };

  saveNote = note => {
    customerNotesApi.save(note, this.props.id)
      .then(() => {
        this.setState({noteActive: false});
        this.getInitalTimelineEvents(false);
      })
      .catch(err => {
        console.error(err);
        events.publish('error.user', {message: this.props.intl.formatMessage({id: 'customer.error.save.notes'})});
      });
  };

  // Called when last timeline event is rendered
  scrollGetTimeline = (e, call) => {
    // Throttle calls to get more previous messages
    const callInfinite = throttle(() => {
      this.setState({
        timelineLoading: true,
      });
      customerTimelineApi.getList(this.props.id, this.state.timelinePageSize, this.state.timelinePageIndex + 1, this.state.sortByAsc)
        .then((timeline) => {
          if (timeline.length === 0) {
            this.setState({
              timelineLoading: false,
              timelineGotAll: true,
            });
          } else {
            this.setState({
              events: [
                ...this.state.events,
                ...timeline,
              ],
              startDate: this.getTimelineStartDate(this.state.events.concat(timeline)),
              timelineLoading: false,
              timelinePageIndex: this.state.timelinePageIndex + 1,
            });
          }
        });
    }, 1000, {'leading': true});

    if (call) {
      callInfinite();
    }

    // Calculate scroll position and pass to children
    const passScrollToChildren = throttle(() => {
      this.setState({
        scroll: window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0

,
      });
    }, 50, {'leading': true});

    passScrollToChildren();
  };

  scrollTopTimeline = () => {
    window.scroll(0, 0);
  };

  stateSetter = propName => value => this.setState({[propName]: value});
  toggleAsendingEvents = () => {
    this.getInitalTimelineEvents(!this.state.sortByAsc);
  };

  render() {
    const timelineEvents = this.getEvents();
    const {intl} = this.props;
    const {formatMessage} = intl;
    const {waterTesters, pools, analyses, note, noteProperties} = this.state;
    const getPoolSystemTypeNameFromAnalysis = (poolID) => {
      if(pools && pools.length > 0) {
        const pool = pools.find(x => x.id === poolID);

        return pool ? `${chemicalBaseTypeNames(pool.typeID, pool.chemicalBaseTypeID, formatMessage)} - ` : '';
      }
      return '';
    };

    const isSaveNoteValid = noteProperties.type === 'system' && pools && pools.length > 0 || // sytem note and there are pools
      noteProperties.type === 'analysis' && analyses && analyses.length > 0 || // analysis note and there analyses
      noteProperties.type === 'customer';
    const isUpdatingNote = !!(note && note.id);
    
    return <div id='timeline'
                className='row'>
      <Loader loaded={this.state.loaded}>
        <div className='col-xs-12'>
          <section className='timeline-filters'>
            <div className={'timeline-collapsed ' + (this.state.showFilters ? 'hide-filter' : '')}>
              <ul>
                <li>
                  <FormattedMessage id="general.timeline"/>
                </li>
                <li>
                  <FormattedMessage id='general.date.range'
                                    values={{
                                      start: moment(this.state.startDate).format(dateTimeFormats.numeralDate),
                                      end: moment(this.state.endDate).format(dateTimeFormats.numeralDate),
                                    }}/>
                </li>
                <li>
                  {this.getEventLabel(this.state.eventFilter)}
                </li>
              </ul>
              <div className={'filter-dropdown-button'}>
                <i onClick={() => this.setState({showFilters: !this.state.showFilters})}
                   className={'bl bl-dropdown'}/>
              </div>
            </div>
            <div className={'timeline-filters-expanded ' + (this.state.showFilters ? '' : 'hide-filter')}>
              <div className='timeline-date'>
                <div className='pull-left'>
                  <div className='filter-title'><FormattedMessage id="general.date.from"/></div>
                  <DatePicker selected={this.state.startDate}
                              dateFormat='L'
                              maxDate={this.state.endDate.clone().subtract(1, 'days')}
                              onChange={this.stateSetter('startDate')}/>
                </div>
                <div className='pull-left'>
                  <div className='filter-title'><FormattedMessage id="general.date.to"/></div>
                  <DatePicker selected={this.state.endDate}
                              dateFormat='L'
                              onChange={this.stateSetter('endDate')}/>
                </div>
              </div>
              <div className='timeline-filter-by'>
                <div className='pull-right filter-selector'>
                  <div className='filter-title'><FormattedMessage id="customer.timeline.event.filter"/></div>
                  <select value={this.state.eventFilter}
                          onChange={e => this.setState({eventFilter: e.target.value})}
                          data-event-category='User'
                          data-event-action='sel-problem-type'>
                    <option value=''>{formatMessage({id: 'customer.timeline.all.events'})}</option>
                    {(this.state.eventTypes || []).map((f,i) => <option key={i}
                                                                    value={f.types}>{f.label}</option>)}
                  </select>
                </div>
              </div>
              {this.state.eventFilter === allNoteTypes.join(',') ?
                <div className='timeline-sub-filter'>
                  <div className='pull-right filter-selector'>
                    <div className='filter-title'><FormattedMessage id="customer.timeline.note.filter"/></div>
                    <select value={this.state.noteFilter}
                            onChange={e => this.setState({noteFilter: e.target.value})}
                            data-event-category='User'
                            data-event-action='sel-note-type'>
                      <option value=''>{formatMessage({id: 'notes.all'})}</option>
                      {(this.state.noteTypes || []).map((f,i) => <option key={i}
                                                                      value={f.types}>{f.label}</option>)}
                    </select>
                  </div>
                </div> :
                null}
              <div className={'filter-dropdown-button'}>
                <i onClick={() => this.setState({showFilters: !this.state.showFilters})}
                   className={'bl bl-dropdown spin-chev'}/>
              </div>
            </div>
          </section>

          <section className='timeline-notes-header'>
            <div className='title'>
              <FormattedMessage id="customer.timeline.title"/>
            </div>
            <button className='btn btn-primary add-button'
                    onClick={() => this.setState({noteActive: true})}>
              <i className='bl bl-add'/>
              <FormattedMessage id="notes.add"/>
            </button>
          </section>

          <section className='timeline-header'>
            {this.state.noteActive ? this.props.intl.formatMessage({id: 'general.notes'}) : null}
          </section>

          {this.state.noteActive && !newNotesEnabled ?
            <section className='timeline-note'>
              <TimelineNote customerID={this.props.id}
                            onSave={this.saveNote}
                            onCancel={() => this.setState({noteActive: false})}/>
            </section> :
            this.state.noteActive ?
              <section className='timeline-note' style={{margin: '0 0 40px'}}>
                <h3 className='notes__modal__date' style={{marginTop: 20, marginBottom: 20}}>{moment().format('LL')}</h3>
                <form onSubmit={this.group.validate}>
                  <Row>
                    <Col md={4}>
                      <EditableTextField dataKey='name'
                                         value={note.name}
                                         editMode={true}
                                         onChange={this.updateNote}
                                         group={this.group}
                                         required
                                         placeholder={formatMessage({id: 'notes.placeholder.name'})} />
                    </Col>

                    <Col md={4}>
                      <EditableSelectField dataKey='author'
                                           editMode={true}
                                           value={note.author}
                                           group={this.group}
                                           required
                                           validate={value => value !== formatMessage({id: 'timeline.notes.select.author'})}
                                           onChange={this.updateNote}
                                           keys={waterTesters.map(x => x.name)}
                                           options={waterTesters.map(x => x.name)}/>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={4}>
                      <EditableSelectField dataKey='type'
                                           editMode={true}
                                           value={noteProperties.type}
                                           group={this.group}
                                           onChange={this.updateNoteProperties}
                                           keys={['customer', 'system', 'analysis']}
                                           options={[noteTypes.customer, noteTypes.system, noteTypes.analysis].map(x => formatMessage({id: x}))}
                                           disabled={isUpdatingNote}/>
                    </Col>

                    {noteProperties.type === 'system' ? <Col md={4}>
                      <EditableSelectField editMode={true}
                                           dataKey={'selectedPool'}
                                           value={noteProperties.selectedPool}
                                           onChange={this.updateNoteProperties}
                                           keys={pools.map(x => x.id)}
                                           group={this.group}
                                           options={pools.map(x => `${chemicalBaseTypeNames(x.typeID, x.chemicalBaseTypeID, formatMessage)} ${
                                            x.nickName ? ` | ${x.nickName}` : ''
                                          }`)}/>
                    </Col> : null}


                    {noteProperties.type === 'analysis' ? <Col md={4}>
                      <EditableSelectField editMode={true}
                                           dataKey={'selectedAnalysis'}
                                           value={noteProperties.selectedAnalysis}
                                           onChange={this.updateNoteProperties}
                                           keys={analyses.map(x => x.id)}
                                           group={this.group}
                                           options={analyses.map(x => `${getPoolSystemTypeNameFromAnalysis(x.poolID)} ${moment(x.created).format('ll - LT')}`)}/>
                    </Col> : null}

                  </Row>
                  <Row>
                    <Col md={12}>
                      <EditableTextArea dataKey='body'
                                        rows={4}
                                        editMode={true}
                                        value={note.body}
                                        onChange={this.updateNote}
                                        group={this.group}
                                        required
                                        className='notes__field__body'
                                        placeholder={formatMessage({id: 'notes.placeholder.details'})} />
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6} mdOffset={6}>
                      <div className='notes__modal__footer'>
                        <button className='btn btn-default' onClick={() => this.setState({
                          noteActive: false,
                          note: new Note({}),
                          noteProperties: {
                            type: 'customer',
                            selectedAnalysis: undefined,
                            selectedPool: undefined,
                          },
                        })}>
                          <FormattedMessage id='general.cancel'/>
                        </button>
                        <button className='btn btn-primary' type='submit' disabled={!isSaveNoteValid}>
                          <FormattedMessage id='general.save' />
                        </button>
                      </div>
                    </Col>
                  </Row>
                </form>
              </section> :
              null}
        </div>
        <section>
          <div className='col-xs-12'>
            <section id='cd-timeline'
                     className='cd-container'>
              {timelineEvents.length > 0 ?
                <div>
                  <div className='direction-sorting'>
                    <i className={'bl icon pointer bl-' + (this.state.sortByAsc ? 'DirectionOfEvents-Up' : 'DirectionOfEvents-Down')}
                       onClick={this.toggleAsendingEvents}/>
                  </div>
                  {timelineEvents}
                  <Loader loaded={!this.state.timelineLoading}/>
                </div> :
                <Loader loaded={!this.state.timelineLoading}>
                  <section className='col-xs-12'>
                    <div className='no-results'>
                      <FormattedMessage id="general.error.no.results"/></div>
                  </section>
                </Loader>}
            </section>
          </div>
          <div className='footer-button'
               onClick={this.scrollTopTimeline}>
            <i className='bl bl-dropdown spin-chev'/>
            <div><FormattedMessage id="customer.timeline.back.top"/></div>
          </div>
        </section>
      </Loader>
    </div>;
  }
}

export default injectIntl(Timeline);
